import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {IResponse} from '@interfaces/response';
import {
  ISharedDevice,
  ISharedDevicePhoto,
  ISingleSharedDevice,
  ISingleSharedDevicePatch,
} from '@interfaces/shared-devices';
import {createHttpParams} from '@shared/functions';
import {HttpClient} from '@angular/common/http';
import {ErrorService} from '@services/error/error.service';
import {catchError, share} from 'rxjs/operators';
import {DeviceForSharingSortType, SharedDevicesSortType} from '../../types/shared-devices.type';
import {OrderType} from '../../types/order.type';
import {IDevice} from '@interfaces/devices';

type ModifiedPatchSharedDevice = Partial<ISingleSharedDevicePatch>;

@Injectable({
  providedIn: 'root'
})

export class SharedDevicesService {

  constructor(private http: HttpClient,
              private errorService: ErrorService) {
  }

  public getSharedDevices(
    {limit, skip = 0, search, sort, order}:
      {
        limit: number, skip: number, search: string,
        sort: SharedDevicesSortType
        order: OrderType;
      }
  ): Observable<IResponse<ISharedDevice>> {
    const params = createHttpParams({
      limit, search, skip, sort, order
    });
    return this.http.get<IResponse<ISharedDevice>>('tv-owner/devices', {params})
      .pipe(
        share(),
        catchError((err) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );
  }

  public getDevicesForSharing(
    {limit, skip = 0, search, sort, order}: {
      limit: number, skip: number, search: string,
      sort: DeviceForSharingSortType
      order: OrderType;
    }
  ): Observable<IResponse<IDevice>> {
    const params = createHttpParams({
      limit, search, skip, sort, order
    });
    return this.http.get<IResponse<IDevice>>('tv-owner/devices/for-sharing', {params}).pipe(
      share(),
      catchError((err) => {
        this.errorService.showError(err, false);
        return throwError(err);
      })
    );
  }

  public patchSharedDevice(data: ModifiedPatchSharedDevice, id: number): Observable<ISingleSharedDevice> {
    return this.http.patch<ISingleSharedDevice>('tv-owner/devices', {...data, id})
      .pipe(
        share(),
        catchError((err) => {
          this.errorService.showError(err, false);
          return throwError(err);
        })
      );
  }

  public uploadPhotosSharedDevice(photo: string, id: number): Observable<IResponse<ISharedDevicePhoto>> {
    return this.http.post<IResponse<ISharedDevicePhoto>>(`tv-owner/devices/${id}/photos`, {photosUrls: [photo]})
      .pipe(
        catchError((err) => {
          this.errorService.showError(err, false);
          return throwError(err);
        })
      );
  }

  public getSharedPhotosByDeviceId(deviceId: number): Observable<IResponse<ISharedDevicePhoto>> {
    return this.http.get<IResponse<ISharedDevicePhoto>>(`tv-owner/devices/${deviceId}/photos`)
      .pipe(
        catchError((err) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );
  }

  public getSharedDeviceById(deviceId: number): Observable<ISingleSharedDevice> {
    return this.http.get<ISingleSharedDevice>(`tv-owner/devices/${deviceId}`).pipe(
      share(),
      catchError((err) => {
        this.errorService.showError(err);
        return throwError(err);
      })
    );
  }

  public removeSharedDevicePhoto(deviceId: number, photoId: number): Observable<any> {
    const params = createHttpParams({
      pIds: photoId
    });
    return this.http.delete(`tv-owner/devices/${deviceId}/photos`, {params})
      .pipe(
        catchError((err) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );
  }
}
